import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, Loader2, ShoppingBag, Download, Music, Play, Pause } from 'lucide-react';
import { getRelease, getReleaseLinks, getImageUrl, formatDateET } from '../utils/api';
import { toast } from 'sonner';
import './ReleasePage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_COLORS = {
  'Spotify': '#1DB954',
  'Apple Music': '#FA2D48',
  'YouTube': '#FF0000',
  'YouTube Music': '#FF0000',
  'iTunes': '#A855F7',
  'Deezer': '#A238FF',
  'TIDAL': '#000000',
  'SoundCloud': '#FF5500',
  'Pandora': '#224099',
  'Beatport': '#94D500',
  'Amazon Music (Streaming)': '#25D1DA',
  'Anghami': '#A238FF',
  'KKBox': '#09CEF6',
  'iHeart Radio': '#C6002B',
};

// Platform logo SVGs
const PlatformLogo = ({ platform, size = 20 }) => {
  const logos = {
    'Spotify': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
      </svg>
    ),
    'Apple Music': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.785-.384-2.172-1.275-.244-.564-.237-1.145-.025-1.72.27-.734.86-1.17 1.6-1.397.263-.08.532-.136.803-.18.472-.077.948-.13 1.417-.214.265-.048.42-.194.47-.456.013-.066.018-.134.018-.2V8.473c0-.282-.124-.418-.406-.371-.563.09-1.125.178-1.686.27l-3.845.62c-.35.056-.453.176-.46.527v7.896c0 .378-.046.75-.2 1.1-.283.637-.774 1.04-1.448 1.235-.348.1-.703.163-1.066.18-.99.045-1.828-.36-2.22-1.3-.22-.526-.218-1.073-.032-1.615.27-.79.9-1.24 1.678-1.45.26-.07.528-.113.796-.152.465-.067.932-.12 1.392-.202.293-.052.456-.22.492-.512.009-.08.012-.16.012-.24V7.15c0-.36.12-.545.467-.605l6.633-1.09c.21-.034.424-.063.638-.083.3-.027.46.104.46.406.005 1.448.003 2.896.003 4.344z"/>
      </svg>
    ),
    'YouTube': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    'YouTube Music': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zM9.684 15.54V8.46L15.816 12l-6.132 3.54z"/>
      </svg>
    ),
    'SoundCloud': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.052-.1-.084-.1zm-.899 1.02c-.053 0-.088.043-.094.098l-.133 1.13.133 1.117c.006.054.041.096.094.096s.088-.042.095-.096l.154-1.117-.154-1.13c-.007-.057-.042-.098-.095-.098zm1.83-.539c-.06 0-.108.047-.115.105l-.212 1.67.212 1.627c.007.058.055.104.115.104.061 0 .108-.046.116-.104l.241-1.627-.241-1.67c-.008-.058-.055-.105-.116-.105zm.945-.139c-.069 0-.12.052-.126.115l-.184 1.95.184 1.894c.006.063.057.114.126.114.07 0 .12-.051.127-.114l.209-1.894-.209-1.95c-.007-.063-.057-.115-.127-.115zm.999-.127c-.077 0-.132.057-.137.128l-.158 2.078.158 2.014c.005.07.06.126.137.126.076 0 .131-.056.137-.126l.181-2.014-.181-2.078c-.006-.071-.061-.128-.137-.128zm1.019-.079c-.084 0-.143.063-.148.14l-.131 2.157.131 2.086c.005.077.064.138.148.138.083 0 .142-.061.148-.138l.15-2.086-.15-2.157c-.006-.077-.065-.14-.148-.14zm.947-.029c-.09 0-.153.065-.158.148l-.105 2.186.105 2.115c.005.083.068.147.158.147.089 0 .153-.064.158-.147l.12-2.115-.12-2.186c-.005-.083-.069-.148-.158-.148zm1.014.016c-.097 0-.165.07-.169.158l-.079 2.17.079 2.097c.004.088.072.156.169.156s.165-.068.169-.156l.091-2.097-.091-2.17c-.004-.088-.072-.158-.169-.158zm.969.074c-.104 0-.176.076-.179.168l-.052 2.096.052 2.024c.003.092.075.167.179.167.103 0 .176-.075.179-.167l.06-2.024-.06-2.096c-.003-.092-.076-.168-.179-.168zm1.006.103c-.111 0-.187.08-.189.178l-.027 1.993.027 1.93c.002.098.078.177.189.177.11 0 .186-.079.189-.177l.031-1.93-.031-1.993c-.003-.098-.079-.178-.189-.178zm.981.172c-.118 0-.199.085-.199.189v1.822l.001 1.759c0 .104.081.188.198.188.117 0 .198-.084.199-.188l.019-1.759-.019-1.822c-.001-.104-.082-.189-.199-.189zm1.013.103c-.125 0-.21.091-.21.2l-.009 1.719.009 1.653c0 .109.085.199.21.199.123 0 .209-.09.21-.199l.01-1.653-.01-1.719c-.001-.109-.087-.2-.21-.2zm.987.147c-.131 0-.221.096-.221.21l-.004 1.572.004 1.509c0 .114.09.209.221.209.13 0 .22-.095.221-.209l.005-1.509-.005-1.572c-.001-.114-.091-.21-.221-.21zm1.025.105c-.138 0-.232.101-.232.22v1.467l.001 1.412c0 .119.094.219.231.219.137 0 .231-.1.232-.219l.002-1.412-.002-1.467c-.001-.119-.095-.22-.232-.22zm.966.158c-.146 0-.243.107-.243.23v1.309l.001 1.26c0 .123.097.229.242.229.144 0 .242-.106.242-.229l.001-1.26V8.88c0-.123-.098-.23-.243-.23zm1.032.109c-.153 0-.255.111-.255.24v1.2l.001 1.155c0 .128.102.239.254.239.152 0 .254-.111.254-.239l.001-1.155v-1.2c0-.129-.102-.24-.255-.24zm.96.175c-.16 0-.268.116-.268.25v1.025l.001.986c0 .133.108.249.267.249.16 0 .268-.116.268-.249l.001-.986v-1.025c0-.134-.108-.25-.269-.25zm1.052.092c-.167 0-.28.122-.28.26v.933l.001.894c0 .138.113.259.279.259.166 0 .28-.121.28-.259l.001-.894v-.933c0-.138-.114-.26-.281-.26zm.939.183c-.175 0-.293.128-.293.27v.75l.001.72c0 .143.118.27.292.27.174 0 .293-.127.293-.27l.001-.72v-.75c0-.142-.119-.27-.294-.27zM24 8.25c0-1.243-1.006-2.25-2.25-2.25h-.09c-.195-2.655-2.407-4.75-5.133-4.75-1.264 0-2.47.47-3.404 1.322a.293.293 0 00-.028.416.292.292 0 00.414.03A5.206 5.206 0 0116.527 2c2.469 0 4.491 1.926 4.681 4.353.034.434.08.855.08 1.147 0 .163.132.295.295.295h.667c.919 0 1.664.746 1.664 1.664 0 .919-.745 1.664-1.664 1.664h-5.227a.295.295 0 000 .59h5.227c1.244 0 2.254-1.01 2.254-2.254 0-.554-.201-1.063-.534-1.459-.076-1.243-.757-2.34-1.72-3"/>
      </svg>
    ),
    'TIDAL': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M12.012 3.992L8.008 7.996 4.004 3.992 0 7.996 4.004 12l4.004-4.004L12.012 12l-4.004 4.004 4.004 4.004 4.004-4.004L12.012 12l4.004-4.004-4.004-4.004zM16.042 7.996l3.979-3.979L24 7.996l-3.979 3.979z"/>
      </svg>
    ),
    'Deezer': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M18.81 4.16v3.03H24V4.16h-5.19zM6.27 8.38v3.027h5.189V8.38h-5.19zm12.54 0v3.027H24V8.38h-5.19zM0 12.604v3.027h5.19v-3.027H0zm6.27 0v3.027h5.189v-3.027h-5.19zm6.27 0v3.027h5.19v-3.027h-5.19zm6.27 0v3.027H24v-3.027h-5.19zM0 16.829v3.027h5.19v-3.027H0zm6.27 0v3.027h5.189v-3.027h-5.19zm6.27 0v3.027h5.19v-3.027h-5.19zm6.27 0v3.027H24v-3.027h-5.19z"/>
      </svg>
    ),
    'Amazon Music (Streaming)': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M13.958 10.09c0 1.232.029 2.256-.591 3.351-.502.891-1.301 1.438-2.186 1.438-1.214 0-1.922-.924-1.922-2.292 0-2.692 2.415-3.182 4.7-3.182v.685zm3.186 7.705a.66.66 0 01-.753.07c-1.057-.878-1.247-1.286-1.826-2.124-1.746 1.781-2.983 2.314-5.245 2.314-2.68 0-4.763-1.653-4.763-4.963 0-2.584 1.4-4.342 3.393-5.202 1.728-.753 4.14-.886 5.985-1.094v-.41c0-.753.058-1.642-.385-2.294-.384-.577-1.12-.815-1.77-.815-1.203 0-2.275.617-2.538 1.897-.054.285-.261.566-.549.58l-3.066-.333c-.259-.058-.546-.266-.472-.66C5.935 1.888 9.058.788 11.852.788c1.401 0 3.232.373 4.337 1.433 1.4 1.308 1.267 3.053 1.267 4.953v4.484c0 1.348.559 1.94 1.084 2.668.185.26.226.571-.008.764-.586.489-1.627 1.398-2.2 1.907l-.188-.202zM21.046 21.146c.138.12.166.335.036.502-.49.624-1.57 1.778-2.14 2.227-.265.21-.61.156-.853-.062-.678-.614-1.197-1.26-1.687-1.873-.278.3-.593.56-.935.78-1.22.81-2.637 1.217-4.076 1.217-2.163 0-4.162-.797-5.73-2.365C3.97 19.876 3.08 17.89 3.08 15.73c0-2.462 1.056-4.67 2.765-6.24 1.71-1.572 4-2.366 6.277-2.283-.01.35-.01.7.01 1.047-.09-.004-.18-.007-.27-.007-1.903 0-3.714.744-5.066 2.09-1.356 1.348-2.104 3.15-2.104 5.075 0 1.922.749 3.725 2.106 5.073 1.354 1.35 3.163 2.094 5.065 2.094 1.903 0 3.712-.745 5.066-2.09.054-.055.107-.11.158-.166.52.68 1.1 1.31 1.72 1.89.29.27.616.5.965.69.168.09.354.14.542.14.25 0 .497-.08.7-.246.096-.078.175-.174.233-.283z"/>
      </svg>
    ),
    'iTunes': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M11.977 23.999c-2.483 0-4.898-.777-6.954-2.262a11.926 11.926 0 01-4.171-5.874 11.999 11.999 0 01.59-7.344 11.971 11.971 0 014.908-5.327A11.911 11.911 0 0118.5 2.58a11.97 11.97 0 014.283 5.607 11.998 11.998 0 01-.227 7.362 11.927 11.927 0 01-4.53 5.617 11.906 11.906 0 01-6.049 2.832zM9.479 4.906c-2.072 0-4.08.61-5.8 1.779A9.618 9.618 0 00.68 10.944a9.65 9.65 0 00-.475 5.905 9.582 9.582 0 003.355 4.723 9.566 9.566 0 005.59 1.819c1.612 0 3.197-.41 4.615-1.2.004-.002 5.447-3.175 5.447-3.175a9.574 9.574 0 003.45-4.469 9.647 9.647 0 00.181-5.92 9.619 9.619 0 00-3.443-4.507 9.56 9.56 0 00-5.544-1.773c-.13 0-.26.003-.39.009l.013-.45zm7.86 7.393c.009.085.009.17.009.255-.001.993-.204 1.976-.6 2.886a7.17 7.17 0 01-1.696 2.408 7.213 7.213 0 01-5.12 2.123c-.193 0-.386-.008-.579-.024a7.257 7.257 0 01-3.028-.914 7.175 7.175 0 01-2.248-1.961 7.204 7.204 0 01-1.242-2.676 7.24 7.24 0 01.178-4.097 7.189 7.189 0 011.755-2.914 7.21 7.21 0 012.746-1.878 7.24 7.24 0 013.35-.41c.194.02.386.048.577.083 1.086.203 2.109.663 2.986 1.342a7.182 7.182 0 012.093 2.543 7.239 7.239 0 01.82 3.234z"/>
      </svg>
    ),
    'Pandora': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm3.568 15.012H9.432V8.988h6.136v6.024z"/>
      </svg>
    ),
    'Beatport': (
      <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
        <path d="M21.429 17.055a7.114 7.114 0 0 1-.794 3.246 6.917 6.917 0 0 1-2.181 2.492 6.698 6.698 0 0 1-3.063 1.163 6.653 6.653 0 0 1-3.239-.434 6.796 6.796 0 0 1-2.668-1.932 7.03 7.03 0 0 1-1.481-2.983 7.124 7.124 0 0 1 .049-3.345 7.015 7.015 0 0 1 1.566-2.937l-4.626 4.73-2.421-2.479 5.201-5.265a3.791 3.791 0 0 0 1.066-2.675V0h3.41v6.613a7.172 7.172 0 0 1-.519 2.794 7.02 7.02 0 0 1-1.559 2.353l-.153.156a6.768 6.768 0 0 1 3.49-1.725 6.687 6.687 0 0 1 3.845.5 6.873 6.873 0 0 1 2.959 2.564 7.118 7.118 0 0 1 1.118 3.8Zm-3.089 0a3.89 3.89 0 0 0-.611-2.133 3.752 3.752 0 0 0-1.666-1.424 3.65 3.65 0 0 0-2.158-.233 3.704 3.704 0 0 0-1.92 1.037 3.852 3.852 0 0 0-1.031 1.955 3.908 3.908 0 0 0 .205 2.213c.282.7.76 1.299 1.374 1.721a3.672 3.672 0 0 0 2.076.647 3.637 3.637 0 0 0 2.635-1.096c.347-.351.622-.77.81-1.231.188-.461.285-.956.286-1.456Z"/>
      </svg>
    ),
  };

  // Return logo if available, otherwise return null
  const logo = logos[platform];
  return logo || null;
};

const ReleasePage = () => {
  const { releaseId } = useParams();
  const [release, setRelease] = useState(null);
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [storeTracks, setStoreTracks] = useState([]);
  const [storeLoading, setStoreLoading] = useState(false);
  const [playingTrack, setPlayingTrack] = useState(null);
  const [audioRef, setAudioRef] = useState(null);

  useEffect(() => {
    fetchRelease();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseId]);

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioRef) {
        audioRef.pause();
      }
    };
  }, [audioRef]);

  const fetchRelease = async () => {
    try {
      const data = await getRelease(releaseId);
      setRelease(data);
      // Fetch streaming links after getting release
      try {
        const linksData = await getReleaseLinks(releaseId);
        setLinks(linksData.links || []);
      } catch {
        setLinks([]);
      }
      // Fetch store tracks for this release
      fetchStoreTracks(data.title, data.artist_name, data.id);
    } catch (error) {
      console.error('Failed to load release', error);
    } finally {
      setLoading(false);
      setLinksLoading(false);
    }
  };

  const fetchStoreTracks = async (releaseTitle, artistName, releaseId) => {
    setStoreLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/store/tracks`);
      if (res.ok) {
        const data = await res.json();
        // API returns array directly, filter tracks that match this release
        const tracks = Array.isArray(data) ? data : (data.tracks || []);
        const matchingTracks = tracks.filter(t => 
          t.release_id === releaseId ||
          t.release_name?.toLowerCase() === releaseTitle?.toLowerCase() ||
          (t.title?.toLowerCase().includes(releaseTitle?.toLowerCase()) && 
           t.artist_name?.toLowerCase() === artistName?.toLowerCase())
        );
        setStoreTracks(matchingTracks);
      }
    } catch (err) {
      console.error('Failed to fetch store tracks:', err);
    } finally {
      setStoreLoading(false);
    }
  };

  const handlePlayPreview = (track) => {
    const trackId = track._id || track.id;
    if (playingTrack === trackId) {
      // Stop playing
      if (audioRef) {
        audioRef.pause();
      }
      setPlayingTrack(null);
    } else {
      // Start playing
      if (audioRef) {
        audioRef.pause();
      }
      const audio = new Audio(track.preview_url);
      audio.play();
      audio.onended = () => setPlayingTrack(null);
      setAudioRef(audio);
      setPlayingTrack(trackId);
    }
  };

  // Pricing (CAD)
  const MP3_PRICE = 1.19;
  const WAV_PRICE = 2.49;

  const handleBuyNow = async (track, format = 'mp3') => {
    // Prompt for email for guest checkout
    let email = sessionStorage.getItem('guestCheckoutEmail');
    if (!email) {
      email = prompt('Please enter your email address for the purchase:');
      if (!email) {
        toast.error('Email is required for checkout');
        return;
      }
      sessionStorage.setItem('guestCheckoutEmail', email);
    }

    // Store track info for after payment
    sessionStorage.setItem('pendingPurchaseTrackIds', JSON.stringify([track._id || track.id]));

    try {
      const res = await fetch(`${API_URL}/api/store/checkout?track_id=${track._id || track.id}&format=${format}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to start checkout');
      }
    } catch (err) {
      toast.error('Failed to start checkout');
    }
  };

  if (loading) {
    return (
      <div className="release-page">
        <div className="loading-container">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  if (!release) {
    return (
      <div className="release-page">
        <div className="error-container">
          <h1>Release Not Found</h1>
          <p>The release you're looking for doesn't exist.</p>
          <Link to="/#releases" className="btn btn-primary">Back to Releases</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="release-page" data-testid="release-page">
      {/* Hero Section */}
      <section className="release-hero">
        <div className="release-hero-bg" style={{ backgroundImage: `url(${getImageUrl(release.artwork)})` }}></div>
        <div className="release-hero-overlay"></div>
      </section>

      {/* Release Content */}
      <section className="release-content">
        <div className="container">
          <div className="release-layout">
            {/* Artwork Side */}
            <div className="release-artwork-side">
              <img src={getImageUrl(release.artwork)} alt={release.title} className="release-artwork-large" data-testid="release-artwork" />
              
              {/* Buy on Beatport Section - Under Image */}
              {links.find(link => link.name === 'Beatport') && (
                <div className="purchase-section" data-testid="purchase-section">
                  <h3><ShoppingBag size={20} /> Buy This Release</h3>
                  <p className="purchase-subtitle">Support the artist by purchasing on Beatport</p>
                  
                  <a 
                    href={links.find(link => link.name === 'Beatport').url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="beatport-buy-btn"
                    data-testid="beatport-buy-btn"
                  >
                    <PlatformLogo platform="Beatport" size={24} />
                    <span>Buy on Beatport</span>
                    <ExternalLink size={16} />
                  </a>
                </div>
              )}
            </div>

            {/* Info Side */}
            <div className="release-info-side">
              <span className="release-genre-tag">{release.genre}</span>
              <h1 data-testid="release-title">{release.title}</h1>
              <p className="release-artist-name">by {release.artist_name}</p>
              
              {release.release_date && (
                <p className="release-date">Released: {formatDateET(release.release_date, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              )}

              {/* Streaming Platform Links */}
              <div className="streaming-section" data-testid="streaming-section">
                <h3>Listen Now</h3>
                <p className="streaming-subtitle">Choose your preferred platform</p>
                
                {linksLoading ? (
                  <div className="links-loading">
                    <Loader2 size={24} className="spin" />
                    <span>Fetching streaming links...</span>
                  </div>
                ) : links.length > 0 ? (
                  <div className="platform-links" data-testid="platform-links">
                    {links.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="platform-btn"
                        style={{ '--platform-color': PLATFORM_COLORS[link.name] || '#666' }}
                        data-testid={`platform-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span className="platform-logo">
                          <PlatformLogo platform={link.name} size={18} />
                        </span>
                        <span className="platform-name">{link.name.replace(' (Streaming)', '')}</span>
                        <ExternalLink size={14} className="external-icon" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <a 
                    href={release.stream_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="stream-external-link"
                    data-testid="fallback-stream-link"
                  >
                    <ExternalLink size={16} />
                    Open on Ditto.fm
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReleasePage;
